import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  Settings, BookOpen, Users, Zap, Target, CheckCircle, 
  AlertTriangle, Info, ExternalLink, Download
} from 'lucide-react';

const AdminConfigurationGuide = () => {
  const [activeSection, setActiveSection] = useState('overview');

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">System Configuration Guide</h1>
        <p className="text-gray-600">Complete setup instructions for BBC QuickList MLM System and Phase 1 configuration</p>
      </div>

      {/* Quick Access Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveSection('phase1')}>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Zap className="h-5 w-5 text-blue-500" />
              <span>Phase 1 Setup</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Configure "Done For You" level with lead capture pages, training materials, and email swaps</p>
            <Badge className="mt-2 bg-blue-100 text-blue-800">Essential</Badge>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveSection('access')}>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-green-500" />
              <span>Access Levels</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Set up Boot Camp, Rookie, Pro, and Enterprise access permissions</p>
            <Badge className="mt-2 bg-green-100 text-green-800">Required</Badge>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveSection('checklist')}>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-purple-500" />
              <span>Configuration Checklist</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Step-by-step checklist to ensure complete system setup</p>
            <Badge className="mt-2 bg-purple-100 text-purple-800">Important</Badge>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeSection} onValueChange={setActiveSection}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="phase1">Phase 1 Setup</TabsTrigger>
          <TabsTrigger value="access">Access Levels</TabsTrigger>
          <TabsTrigger value="onboarding">Member Onboarding</TabsTrigger>
          <TabsTrigger value="checklist">Checklist</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>BBC QuickList Configuration Overview</CardTitle>
              <CardDescription>Essential setup for your MLM system and member access levels</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-lg mb-3">System Components</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Lead Capture Pages System</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Marketing Content Library</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Member Access Controls</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Training Materials</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Email Swap System</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-3">Access Tiers</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2">
                      <Badge className="bg-yellow-100 text-yellow-800">Boot Camp</Badge>
                      <span>Basic access</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Badge className="bg-green-100 text-green-800">Rookie</Badge>
                      <span>Done-for-you level</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Badge className="bg-purple-100 text-purple-800">Pro</Badge>
                      <span>Advanced features</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Badge className="bg-red-100 text-red-800">Enterprise</Badge>
                      <span>Full access</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Phase 1 Setup Tab */}
        <TabsContent value="phase1" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Phase 1: "Done For You" Configuration</CardTitle>
              <CardDescription>Set up complete member experience with all materials included</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Info className="h-5 w-5 text-blue-500" />
                  <h3 className="font-semibold">Phase 1 Should Include:</h3>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>All Lead Capture Page Templates</li>
                  <li>Complete Marketing Content Library (40+ angles)</li>
                  <li>Rookie Training Materials</li>
                  <li>Email Swipe Copies</li>
                  <li>Sales Materials and Scripts</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Lead Capture Setup</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Navigate to Admin → Marketing Manager</li>
                      <li>Set template access levels</li>
                      <li>Configure marketing content permissions</li>
                      <li>Test template generation</li>
                    </ol>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Training Materials</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Upload rookie training modules</li>
                      <li>Configure email swap system</li>
                      <li>Set up sales materials library</li>
                      <li>Create member onboarding sequence</li>
                    </ol>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <h3 className="font-semibold">Important Notes:</h3>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Boot Camp members get 5 basic templates</li>
                  <li>Rookie members get all templates (unlimited)</li>
                  <li>Marketing angles are filtered by access level</li>
                  <li>Training materials auto-unlock based on membership tier</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access Levels Tab */}
        <TabsContent value="access" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Badge className="bg-yellow-100 text-yellow-800">Boot Camp</Badge>
                  <span>Free Tier</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li>• 5 basic lead capture templates</li>
                  <li>• 10 basic marketing angles</li>
                  <li>• Phase 1 training materials</li>
                  <li>• Basic support access</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Badge className="bg-green-100 text-green-800">Rookie</Badge>
                  <span>Phase 1 Done-For-You</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li>• All lead capture templates (unlimited)</li>
                  <li>• Full marketing library (40+ angles)</li>
                  <li>• Email swipe copies</li>
                  <li>• Sales materials</li>
                  <li>• Advanced training modules</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Badge className="bg-purple-100 text-purple-800">Pro</Badge>
                  <span>Advanced Features</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li>• All Rookie features</li>
                  <li>• Custom template creation</li>
                  <li>• Advanced marketing analytics</li>
                  <li>• Priority support</li>
                  <li>• Monthly strategy calls</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Badge className="bg-red-100 text-red-800">Enterprise</Badge>
                  <span>Full Access</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li>• All Pro features</li>
                  <li>• White-label rights</li>
                  <li>• Custom branding</li>
                  <li>• Dedicated account manager</li>
                  <li>• Revenue sharing opportunities</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Member Onboarding Tab */}
        <TabsContent value="onboarding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Member Onboarding Process</CardTitle>
              <CardDescription>Automated workflow for new member success</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full p-3 w-12 h-12 mx-auto mb-3">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <h3 className="font-semibold">Automatic Access Grant</h3>
                  <ul className="text-sm mt-2 space-y-1">
                    <li>All lead capture templates</li>
                    <li>Full marketing library</li>
                    <li>Training materials</li>
                  </ul>
                </div>
                
                <div className="text-center">
                  <div className="bg-green-100 rounded-full p-3 w-12 h-12 mx-auto mb-3">
                    <span className="text-green-600 font-bold">2</span>
                  </div>
                  <h3 className="font-semibold">Welcome Sequence</h3>
                  <ul className="text-sm mt-2 space-y-1">
                    <li>Getting started email</li>
                    <li>Training schedule</li>
                    <li>First month action plan</li>
                  </ul>
                </div>
                
                <div className="text-center">
                  <div className="bg-purple-100 rounded-full p-3 w-12 h-12 mx-auto mb-3">
                    <span className="text-purple-600 font-bold">3</span>
                  </div>
                  <h3 className="font-semibold">Support Materials</h3>
                  <ul className="text-sm mt-2 space-y-1">
                    <li>Quick start guide</li>
                    <li>Video walkthroughs</li>
                    <li>FAQ access</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Checklist Tab */}
        <TabsContent value="checklist" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Configuration Checklist</CardTitle>
              <CardDescription>Complete this checklist to ensure proper system setup</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Essential Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Admin access configured</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Member access levels defined</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Lead capture templates organized</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Marketing library permissions set</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Training materials uploaded</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Email swap system activated</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Sales materials library created</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Member onboarding sequence setup</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Progress tracking enabled</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Support system operational</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-lg mb-2">Next Steps</h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Test the system with test member accounts</li>
                    <li>Verify access levels work correctly</li>
                    <li>Create member welcome materials</li>
                    <li>Begin accepting Phase 1 members</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Download Link */}
      <Card className="mt-8">
        <CardContent className="p-6 text-center">
          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Detailed Configuration Guide</h3>
          <p className="text-gray-600 mb-4">
            For the complete step-by-step configuration instructions, refer to the detailed guide.
          </p>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            View MLM_SYSTEM_CONFIG_GUIDE.md
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminConfigurationGuide;